import { useEffect, useState } from "react";
import { Badge, Button, Dialog, SkeletonRows } from "./ui";
import { exportService, type ExportReport } from "../services/export.service";
import { backupService, type BackupReport } from "../services/backup.service";

/**
 * Exportar projeto: mostra o relatório de independência e permite baixar o .zip.
 * O pacote nunca inclui .env real, chaves, node_modules, vendor ou logs.
 */
export default function ExportProjectDialog({
  projectId,
  open,
  onClose,
}: {
  projectId: string;
  open: boolean;
  onClose: () => void;
}) {
  const [report, setReport] = useState<ExportReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [backup, setBackup] = useState<BackupReport | null>(null);
  const [dumping, setDumping] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    exportService
      .inspect(projectId)
      .then(setReport)
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
    backupService
      .inspect(projectId)
      .then(setBackup)
      .catch(() => setBackup(null));
  }, [open, projectId]);

  const requestDump = () => {
    setDumping(true);
    backupService
      .dumpDatabase(projectId)
      .then(() => backupService.inspect(projectId).then(setBackup))
      .catch((err: Error) => setError(err.message))
      .finally(() => setDumping(false));
  };

  const missing = Object.entries(report?.required_files ?? {}).filter(([, ok]) => !ok);
  const blocking = report?.scan.blocking ?? [];
  const warnings = report?.scan.warnings ?? [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title="Exportar projeto"
      description="Baixe todo o código para rodar em qualquer lugar, sem depender do Okton Builder."
    >
      {loading && <SkeletonRows rows={4} />}
      {error && <p className="text-sm text-danger">{error}</p>}

      {report && !loading && (
        <div className="space-y-4 text-sm">
          <div className="flex items-center gap-2">
            <Badge tone={blocking.length === 0 ? "ok" : "danger"}>
              {blocking.length === 0 ? "Roda sem o Builder" : "Dependência encontrada"}
            </Badge>
            <span className="text-xs text-ink-muted">
              {report.scan.files_scanned} arquivos verificados
            </span>
          </div>

          {missing.length > 0 && (
            <div className="rounded-md border border-warn/40 bg-warn/10 p-3 text-xs text-ink">
              <p className="font-semibold">Faltam arquivos essenciais no projeto:</p>
              <ul className="mt-1 list-inside list-disc font-mono">
                {missing.map(([file]) => (
                  <li key={file}>{file}</li>
                ))}
              </ul>
            </div>
          )}

          {blocking.length > 0 && (
            <div className="rounded-md border border-danger/40 bg-danger/10 p-3 text-xs">
              <p className="font-semibold text-danger">
                Referências ao Builder ou a serviços externos no código:
              </p>
              <ul className="mt-1 max-h-32 space-y-0.5 overflow-y-auto font-mono text-ink-muted">
                {blocking.slice(0, 20).map((hit, index) => (
                  <li key={`${hit.file}-${hit.line}-${index}`}>
                    {hit.file}:{hit.line} — {hit.excerpt}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {warnings.length > 0 && (
            <p className="text-xs text-ink-muted">
              {warnings.length} menção(ões) apenas em documentação ou comentários — não afetam o
              funcionamento do projeto.
            </p>
          )}

          <div className="rounded-md border border-line bg-surface-raised/50 p-3 text-xs text-ink-muted">
            <p className="font-semibold text-ink">O pacote inclui</p>
            <p>
              frontend, backend, infra, migrations, docker-compose, README, .env.example, arquivos do
              Composer e do npm e a documentação.
            </p>
            <p className="mt-2 font-semibold text-ink">O pacote não inclui</p>
            <p>.env real, chaves e tokens, node_modules, vendor, logs, caches e temporários.</p>
            <p className="mt-2 font-semibold text-ink">Para rodar depois de baixar</p>
            <pre className="mt-1 font-mono text-[11px] text-ink">
              cp .env.example .env{"\n"}docker compose up -d --build
            </pre>
          </div>

          <div className="rounded-md border border-line bg-surface-2 p-3 text-xs text-ink-muted">
            <p className="font-semibold text-ink">Backup do projeto</p>
            <p className="mt-1">
              Histórico Git completo (bundle clonável), arquivos enviados e o dump do banco de
              desenvolvimento. Segredos ficam de fora.
            </p>
            <ul className="mt-2 space-y-1">
              <li>Repositório: {backup?.repository.available ? "disponível" : "indisponível"}</li>
              <li>
                Uploads:{" "}
                {backup?.uploads.available
                  ? Object.entries(backup.uploads.directories)
                      .map(([dir, count]) => `${dir} (${count})`)
                      .join(", ")
                  : "nenhum arquivo encontrado"}
              </li>
              <li>
                Banco de desenvolvimento:{" "}
                {backup?.database.available
                  ? `dump de ${backup.database.generated_at ?? "data desconhecida"}`
                  : "sem dump gerado"}
              </li>
            </ul>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button variant="secondary" onClick={requestDump} disabled={dumping}>
                {dumping ? "Gerando dump..." : "Gerar dump do banco"}
              </Button>
              <a
                href={backupService.downloadUrl(projectId)}
                className="inline-flex h-10 items-center rounded-md border border-line px-4 text-sm font-medium text-ink hover:bg-surface-3"
              >
                Baixar backup
              </a>
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              Fechar
            </Button>
            <a
              href={exportService.downloadUrl(projectId)}
              className="inline-flex h-10 items-center rounded-md bg-brand px-4 text-sm font-medium text-brand-ink hover:brightness-110"
            >
              Baixar ZIP
            </a>
          </div>
        </div>
      )}
    </Dialog>
  );
}
